<template>
    <div class="full-width">
        <q-card
            class="full-width full-height no-shadow"
            :class="{
                'border-grey': this.hasBorder,
                'highlighted-phase-card': this.isHighlighted
            }"
            @click="toggleMergeCheckbox"
            :square="hasSquareBorder"
        >
            <q-form @submit="submit">
                <q-card-section class="q-pa-none">
                    <!-- Phase content -->
                    <div class="row" v-if="!inlineEditMode">
                        <div
                            class="col background-grey"
                            :style="getCardBorderClass"
                        >
                            <div class="row full-height">
                                <div class="col-xs-12 col-sm-4 col-md-3">
                                    <!-- Status -->
                                    <div
                                        class="flex items-center justify-start full-height q-py-md q-pl-md no-wrap"
                                        :class="getPhaseStatusBgClass(phase)"
                                    >
                                        <q-checkbox
                                            v-if="
                                                isMergeMode || isBulkUpdateMode
                                            "
                                            v-model="selected"
                                            color="accent"
                                        />
                                        <q-icon
                                            :color="
                                                ASSET_STATUS_TO_STYLE[phase][
                                                    'color'
                                                ]
                                            "
                                            :name="
                                                ASSET_STATUS_TO_STYLE[phase][
                                                    'icon'
                                                ]
                                            "
                                            class="q-mr-sm"
                                            size="sm"
                                            square
                                            outline
                                        >
                                            <q-tooltip>
                                                {{
                                                    ASSET_STATUS_TO_STYLE[
                                                        phase
                                                    ]['label']
                                                }}
                                            </q-tooltip>
                                        </q-icon>
                                        <div
                                            v-if="phase === 'running'"
                                            class="ellipsis"
                                        >
                                            {{ $t('Running') }}
                                        </div>
                                        <div
                                            v-else-if="causeName"
                                            class="ellipsis"
                                        >
                                            <q-tooltip>
                                                {{ causeName }}
                                            </q-tooltip>
                                            {{ causeName }}
                                        </div>
                                        <div v-else>
                                            <div
                                                v-if="
                                                    phase === 'in_downtime' ||
                                                    phase === 'changeover'
                                                "
                                            >
                                                <div v-if="isMajor">
                                                    <missing-cause-label
                                                        v-model="
                                                            causeSelectProps.selection
                                                        "
                                                        :phaseId="phaseId"
                                                        :phaseUUID="phaseUUID"
                                                        :phaseEnd="end"
                                                        @refreshList="
                                                            refreshList
                                                        "
                                                        @clearSelection="
                                                            causeSelectProps.selection =
                                                                null
                                                        "
                                                        :baseURL="
                                                            causeSelectProps.url
                                                        "
                                                        :queryParams="
                                                            getCauseQueryParams
                                                        "
                                                        :label="
                                                            causeSelectProps.label
                                                        "
                                                        :dialogTitle="
                                                            causeSelectProps.dialogTitle
                                                        "
                                                        :searchLabel="
                                                            causeSelectProps.searchLabel
                                                        "
                                                        :isFlat="true"
                                                        :isDense="true"
                                                        :hideIcon="true"
                                                    >
                                                        <template
                                                            v-slot:item="data"
                                                        >
                                                            <cause-card
                                                                @select="
                                                                    select(
                                                                        data
                                                                            .props
                                                                            .row,
                                                                        causeSelectProps
                                                                    )
                                                                "
                                                                :name="
                                                                    data.props
                                                                        .row
                                                                        .name
                                                                "
                                                                :lossType="
                                                                    data.props
                                                                        .row
                                                                        .type_of_loss
                                                                "
                                                                :categoryName="
                                                                    data.props
                                                                        .row
                                                                        .category_name
                                                                "
                                                                :planned="
                                                                    data.props
                                                                        .row
                                                                        .planned
                                                                "
                                                                :description="
                                                                    data.props
                                                                        .row
                                                                        .description
                                                                "
                                                                :targetDowntime="
                                                                    data.props
                                                                        .row
                                                                        .target_down_time
                                                                "
                                                                :isSelected="
                                                                    causeSelectProps.selection &&
                                                                    causeSelectProps
                                                                        .selection
                                                                        .value ===
                                                                        data
                                                                            .props
                                                                            .row
                                                                            .id
                                                                "
                                                                :isSelectable="
                                                                    true
                                                                "
                                                                :equipmentFailure="
                                                                    data.props
                                                                        .row
                                                                        .equipment_failure
                                                                "
                                                            />
                                                        </template>
                                                    </missing-cause-label>
                                                </div>
                                                <div v-else>
                                                    {{ $t('Minor Downtime') }}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-xs-12 col-sm-8 col-md-9">
                                    <div class="row q-pa-md">
                                        <div
                                            class="col-xs-12 col-sm-6 col-md-6"
                                        >
                                            <div class="row">
                                                <!-- Started at -->
                                                <div
                                                    class="col-xs-12 col-sm-12 col-md-6"
                                                >
                                                    <div class="phase-label">
                                                        {{ $t('Started at') }}
                                                    </div>
                                                    <div>
                                                        {{
                                                            getMonthDayTime(
                                                                start,
                                                                userLocale,
                                                                timezone
                                                            )
                                                        }}
                                                    </div>
                                                </div>
                                                <!-- Duration -->
                                                <div
                                                    class="col-xs-12 col-sm-12 col-md-6"
                                                >
                                                    <div>
                                                        <div
                                                            class="phase-label"
                                                            :class="{
                                                                'q-mt-md':
                                                                    $q.screen.lt
                                                                        .md,
                                                                'align-duration-label':
                                                                    !end
                                                            }"
                                                        >
                                                            {{ $t('Duration') }}
                                                        </div>
                                                        <div
                                                            class="flex items-center no-wrap"
                                                        >
                                                            <span>
                                                                {{
                                                                    phaseDuration
                                                                }}
                                                            </span>
                                                            <q-chip
                                                                v-if="!end"
                                                                :color="
                                                                    ASSET_STATUS_TO_STYLE[
                                                                        phase
                                                                    ]['color']
                                                                "
                                                                text-color="white"
                                                                size="sm"
                                                                :label="
                                                                    $t(
                                                                        'Ongoing'
                                                                    )
                                                                "
                                                            />
                                                            <!-- No Product Duration -->
                                                            <q-icon
                                                                v-if="
                                                                    phase !==
                                                                        'running' &&
                                                                    end !== null
                                                                "
                                                                name="fa-regular fa-info-circle"
                                                                class="q-ml-xs"
                                                                color="grey"
                                                                size="xs"
                                                            >
                                                                <q-tooltip>
                                                                    <div>
                                                                        {{
                                                                            $t(
                                                                                'No Product Duration'
                                                                            )
                                                                        }}
                                                                    </div>
                                                                    <div>
                                                                        {{
                                                                            productDuration
                                                                        }}
                                                                    </div>
                                                                </q-tooltip>
                                                            </q-icon>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div
                                            class="col-xs-12 col-sm-6 col-md-6"
                                        >
                                            <div
                                                class="row"
                                                v-if="phase === 'running'"
                                            >
                                                <div
                                                    class="col-xs-12 col-sm-12 col-md-6"
                                                >
                                                    <!-- Product count -->
                                                    <div
                                                        :class="{
                                                            'align-value-right':
                                                                $q.screen.gt.xs,
                                                            'q-mt-md':
                                                                $q.screen.lt.sm
                                                        }"
                                                    >
                                                        <div
                                                            class="phase-label"
                                                        >
                                                            {{
                                                                $t(
                                                                    'Product Count'
                                                                )
                                                            }}
                                                        </div>
                                                        <div>
                                                            {{
                                                                productCount ||
                                                                '-'
                                                            }}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div
                                                    class="col-xs-12 col-sm-12 col-md-6"
                                                >
                                                    <!-- Product Quantity -->
                                                    <div
                                                        :class="{
                                                            'align-value-right':
                                                                $q.screen.gt.xs,
                                                            'q-mt-md':
                                                                $q.screen.lt.md
                                                        }"
                                                    >
                                                        <div
                                                            class="phase-label"
                                                        >
                                                            {{
                                                                $t(
                                                                    'Product Quantity'
                                                                )
                                                            }}
                                                        </div>
                                                        <div>
                                                            {{
                                                                productQuantity ||
                                                                '-'
                                                            }}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div v-else class="row">
                                                <!-- Description -->
                                                <div
                                                    v-if="description"
                                                    class="col-xs-12 col-sm-12 col-md-8 q-pr-md"
                                                    :class="{
                                                        'q-mt-md':
                                                            $q.screen.lt.sm
                                                    }"
                                                >
                                                    <div class="phase-label">
                                                        {{ $t('Description') }}
                                                    </div>
                                                    <div
                                                        v-if="
                                                            description.length <=
                                                            descriptionMaxLength
                                                        "
                                                    >
                                                        {{ description }}
                                                    </div>
                                                    <div v-else>
                                                        {{
                                                            description.substring(
                                                                0,
                                                                descriptionMaxLength
                                                            )
                                                        }}...
                                                        <span
                                                            @click="
                                                                $emit(
                                                                    'showDescription',
                                                                    description
                                                                )
                                                            "
                                                            class="text-accent cursor-pointer"
                                                        >
                                                            {{
                                                                $t('read more')
                                                            }}
                                                        </span>
                                                    </div>
                                                </div>
                                                <!-- Media -->
                                                <div
                                                    class="col-xs-6 col-sm-6 col-md-2"
                                                    :class="{
                                                        'q-mt-md':
                                                            $q.screen.lt.md
                                                    }"
                                                >
                                                    <div
                                                        v-if="
                                                            isMediaUploadEnabled
                                                        "
                                                        class="full-height flex items-center"
                                                    >
                                                        <media-gallery-button
                                                            :mediaIds="
                                                                phaseMedia
                                                            "
                                                            :phaseId="phaseId"
                                                        />
                                                    </div>
                                                </div>
                                                <!-- Proximity reading -->
                                                <div
                                                    class="col-xs-6 col-sm-6 col-md-2"
                                                    :class="{
                                                        'q-mt-md':
                                                            $q.screen.lt.md
                                                    }"
                                                >
                                                    <div
                                                        v-if="
                                                            hasProximitySensor &&
                                                            proximityReading
                                                        "
                                                        class="full-height flex items-center"
                                                    >
                                                        <q-icon
                                                            :name="
                                                                proximityReading
                                                                    ? 'far fa-sensor-on'
                                                                    : 'far fa-sensor'
                                                            "
                                                            class="q-mx-xs"
                                                            size="sm"
                                                            :color="
                                                                proximityReading ===
                                                                1
                                                                    ? 'accent'
                                                                    : 'secondary'
                                                            "
                                                        >
                                                            <q-tooltip
                                                                anchor="bottom middle"
                                                                self="top middle"
                                                            >
                                                                {{
                                                                    getProximityLabel(
                                                                        proximityReading
                                                                    )
                                                                }}
                                                            </q-tooltip>
                                                        </q-icon>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div
                            class="col-auto flex flex-center q-pa-sm action-buttons"
                        >
                            <q-btn
                                @click="inlineEditMode = true"
                                :disable="
                                    !isPhaseEditable(end) ||
                                    $userCannot('edit', 'phases')
                                "
                                icon="fas fa-edit"
                                color="accent"
                                size="md"
                                class="q-mr-md"
                                outline
                            >
                                <q-tooltip
                                    v-if="getUneditableErrorMessage(end)"
                                >
                                    {{ getUneditableErrorMessage(end) }}
                                </q-tooltip>
                            </q-btn>
                            <q-btn
                                @click="$emit('split')"
                                :disable="$userCannot('split', 'phases')"
                                :color="
                                    $userCan('split', 'phases') && isSplittable
                                        ? 'warning'
                                        : 'grey'
                                "
                                icon="call_split"
                                size="md"
                                outline
                            >
                                <q-tooltip
                                    v-if="!isSplittable"
                                    anchor="bottom middle"
                                    self="top middle"
                                >
                                    {{
                                        $t(
                                            'This phase is too short to be split. A phase must be at least be 3 seconds long to split.'
                                        )
                                    }}
                                </q-tooltip>
                                <q-tooltip
                                    v-else
                                    anchor="bottom middle"
                                    self="top middle"
                                >
                                    {{ $t('Split phase') }}
                                </q-tooltip>
                            </q-btn>
                        </div>
                    </div>

                    <!-- Edit mode -->
                    <div class="row" v-if="inlineEditMode">
                        <!-- Status column -->
                        <div
                            class="col-1 flex items-center justify-center"
                            :class="getPhaseStatusBgClass(phase)"
                        >
                            <q-icon
                                :color="ASSET_STATUS_TO_STYLE[phase]['color']"
                                :name="ASSET_STATUS_TO_STYLE[phase]['icon']"
                                class="q-mx-none q-mb-sm"
                                size="sm"
                                square
                                outline
                            >
                                <q-tooltip>
                                    {{ ASSET_STATUS_TO_STYLE[phase]['label'] }}
                                </q-tooltip>
                            </q-icon>
                        </div>
                        <!-- Fields column (breaks down to 2 columns on medium devices, single column on small devices) -->
                        <div class="col-8 q-pa-md">
                            <banner
                                v-if="!end && inlineEditMode"
                                class="q-mb-md q-mr-md"
                                :is-visible="!end && inlineEditMode"
                                type="info"
                                :message="$t(editPhaseInfoMessage)"
                            />

                            <!-- EDIT MODE START -->

                            <div
                                class="row q-col-gutter-x-md"
                                v-if="inlineEditMode"
                            >
                                <!-- First column of fields -->
                                <div
                                    class="col-xs-12 col-md-6 q-col-gutter-x-md"
                                    :class="{
                                        'q-col-gutter-y-lg': $q.screen.lt.md
                                    }"
                                >
                                    <!-- Date range field -->
                                    <date-time-picker
                                        v-model="form.start"
                                        :label="$t('Start')"
                                        :disable="true"
                                    />
                                    <date-time-picker
                                        v-model="form.end"
                                        :label="$t('End')"
                                        :disable="true"
                                    />
                                    <!-- Status field 
                                         Moved to the third row to align the fields on both columns
                                         because the date range fields and the product and cause
                                         fields have the same space below reserved for error messages.
                                    -->
                                    <q-select
                                        v-model="form.status"
                                        :label="$t('Status')"
                                        :options="statusOptions"
                                        outlined
                                        lazy-rules
                                        :disable="
                                            !isPhaseEditable(end) ||
                                            isPhaseInProgress(end)
                                        "
                                        color="accent"
                                    >
                                        <!-- Option style -->
                                        <template v-slot:option="scope">
                                            <q-item
                                                v-bind="scope.itemProps"
                                                @click="scope.itemEvents"
                                            >
                                                <q-item-section class="row">
                                                    <div>
                                                        <q-icon
                                                            :color="
                                                                ASSET_STATUS_TO_STYLE[
                                                                    scope.opt
                                                                        .value
                                                                ]['color']
                                                            "
                                                            name="fas fa-circle"
                                                            class="q-mr-sm"
                                                        ></q-icon>
                                                        {{
                                                            $t(scope.opt.label)
                                                        }}
                                                    </div>
                                                </q-item-section>
                                            </q-item>
                                        </template>
                                        <!-- Selected item style -->
                                        <template v-slot:selected>
                                            <q-icon
                                                :color="form.status['color']"
                                                style="margin-bottom: 2px"
                                                name="fas fa-circle"
                                            ></q-icon>
                                            <div style="margin-left: 5px">
                                                {{ $t(form.status['label']) }}
                                            </div>
                                        </template>
                                    </q-select>
                                </div>

                                <!-- Second column of fields -->
                                <div
                                    class="col-xs-12 col-md-6 q-col-gutter-x-md"
                                    :class="{
                                        'q-mt-lg': $q.screen.lt.md
                                    }"
                                >
                                    <!-- Product field -->
                                    <table-select
                                        v-if="form.status.value !== 'stopped'"
                                        v-model="form.product"
                                        @confirmSelection="
                                            form.product =
                                                productSelectProps.selection
                                        "
                                        :baseURL="productSelectProps.url"
                                        :queryParams="
                                            productSelectProps.queryParams
                                        "
                                        :label="productSelectProps.label"
                                        :dialogTitle="
                                            productSelectProps.dialogTitle
                                        "
                                        :searchLabel="
                                            productSelectProps.searchLabel
                                        "
                                        :rules="formRules.product"
                                        row-key="id"
                                        :disable="
                                            !isPhaseEditable(end) ||
                                            isPhaseInProgress(end)
                                        "
                                    >
                                        <template v-slot:item="data">
                                            <product-card
                                                @select="
                                                    productSelectProps.selection =
                                                        {
                                                            label: data.props
                                                                .row.name,
                                                            value: data.props
                                                                .row.product
                                                        }
                                                "
                                                :name="data.props.row.name"
                                                :code="data.props.row.code"
                                                :description="
                                                    data.props.row.description
                                                "
                                                :unit-measure="
                                                    data.props.row.unit_measure
                                                "
                                                :unit-packing="
                                                    data.props.row.unit_packing
                                                "
                                                :isSelected="
                                                    productSelectProps.selection &&
                                                    productSelectProps.selection
                                                        .value ===
                                                        data.props.row.product
                                                "
                                                :isSelectable="true"
                                            />
                                        </template>
                                    </table-select>

                                    <!-- Cause field -->
                                    <cause-table-select
                                        v-if="
                                            form.status.value ===
                                                'changeover' ||
                                            form.status.value ===
                                                'in_downtime' ||
                                            form.status.value === 'stopped'
                                        "
                                        v-model="form.cause"
                                        @confirmSelection="
                                            form.cause =
                                                causeSelectProps.selection
                                        "
                                        :baseURL="causeSelectProps.url"
                                        :queryParams="getCauseQueryParams"
                                        :label="causeSelectProps.label"
                                        :dialogTitle="
                                            causeSelectProps.dialogTitle
                                        "
                                        :searchLabel="
                                            causeSelectProps.searchLabel
                                        "
                                        :facilityId="
                                            causeSelectProps.facilityId
                                        "
                                        :rules="formRules.cause"
                                        row-key="id"
                                        :disable="!isPhaseEditable(end)"
                                        :noDataLabel="
                                            getNoShutdownCausesMessage
                                        "
                                    >
                                        <template v-slot:item="data">
                                            <cause-card
                                                @select="
                                                    causeSelectProps.selection =
                                                        {
                                                            label: data.props
                                                                .row.name,
                                                            value: data.props
                                                                .row.id
                                                        }
                                                "
                                                :name="data.props.row.name"
                                                :lossType="
                                                    data.props.row.type_of_loss
                                                "
                                                :categoryName="
                                                    data.props.row.category_name
                                                "
                                                :planned="
                                                    data.props.row.planned
                                                "
                                                :description="
                                                    data.props.row.description
                                                "
                                                :targetDowntime="
                                                    data.props.row
                                                        .target_down_time
                                                "
                                                :isSelected="
                                                    causeSelectProps.selection &&
                                                    causeSelectProps.selection
                                                        .value ===
                                                        data.props.row.id
                                                "
                                                :isSelectable="true"
                                                :equipmentFailure="
                                                    data.props.row
                                                        .equipment_failure
                                                "
                                            />
                                        </template>
                                    </cause-table-select>
                                    <!-- Description field -->
                                    <q-input
                                        v-model="form.description"
                                        :label="$t('Description')"
                                        :input-style="'min-height: 25px; height: 25px;'"
                                        type="textarea"
                                        outlined
                                        color="accent"
                                        :disable="!isPhaseEditable(end)"
                                    />
                                </div>
                            </div>
                            <div
                                class="row q-mt-md q-mr-md q-gutter-lg"
                                v-if="inlineEditMode"
                            >
                                <div
                                    v-if="isMediaUploadEnabled"
                                    class="col-sm-1 col-xs-12"
                                    :class="{ 'q-mb-md': $q.screen.lt.sm }"
                                >
                                    <!-- Add media -->
                                    <media-input-manager
                                        :mediaCount="
                                            editableMedia.length +
                                            updatedMedia.add.length -
                                            updatedMedia.remove.length
                                        "
                                        @update:add="
                                            medias =>
                                                updatedMedia.add.push(...medias)
                                        "
                                    />
                                </div>
                                <div
                                    v-if="isMediaUploadEnabled"
                                    class="col-sm-10 col-xs-12"
                                >
                                    <!-- Display media with remove button -->
                                    <media-grid-manager
                                        :phaseId="phaseId"
                                        :existingMedias="editableMedia"
                                        :newMedias="updatedMedia.add"
                                        @update:removeExistingMedia="
                                            mediaId =>
                                                updatedMedia.remove.push(
                                                    mediaId
                                                )
                                        "
                                        @update:removeNewMedia="
                                            newMedias =>
                                                (updatedMedia.add = newMedias)
                                        "
                                        :inline="true"
                                    />
                                </div>
                            </div>
                        </div>

                        <!-- Action column -->
                        <q-card-actions
                            class="col-3 flex justify-center items-center"
                        >
                            <q-btn
                                @click="
                                    () => {
                                        inlineEditMode = false;
                                    }
                                "
                                icon="fas fa-times"
                                color="negative"
                                size="md"
                                outline
                            >
                                <q-tooltip>
                                    {{ $t('Cancel') }}
                                </q-tooltip>
                            </q-btn>
                            <q-btn
                                type="submit"
                                :loading="loading"
                                :disable="!formHasChanged"
                                icon="fas fa-check"
                                color="positive"
                                text-color="white"
                                size="md"
                            >
                                <q-tooltip>
                                    {{ $t('Save') }}
                                </q-tooltip>
                            </q-btn>
                        </q-card-actions>
                    </div>
                </q-card-section>
            </q-form>
        </q-card>
    </div>
</template>
<script>
import { ASSET_STATUS_TO_STYLE, STATUS_OPTIONS } from '@models/assets.js';
import {
    PROXIMITY_SENSOR_OPTIONS,
    PROXIMITY_SENSOR_LABELS
} from '@models/devices.js';
import DateTimePicker from '@components/DateTimePicker.vue';
import CauseCard from '@components/CauseCard.vue';
import ProductCard from '@components/ProductCard.vue';
import TableSelect from '@components/TableSelect.vue';
import CauseTableSelect from '@components/CauseTableSelect.vue';

import { getLocaleFormat } from '@models/timeformats.js';

import MissingCauseLabel from '@components/MissingCauseLabel.vue';
import {
    isPhaseEditable,
    isPhaseInProgress,
    getUneditableErrorMessage
} from '@utils/phaseChecker.js';
import { editPhaseInfoMessage } from '@utils/getMessages.js';
import Banner from '@components/Banner.vue';
import MediaGalleryButton from '@components/MediaGalleryButton.vue';
import MediaGridManager from '@components/MediaGridManager.vue';
import MediaInputManager from '@components/MediaInputManager.vue';
import { updatePhaseMedia } from '@utils/mediaManager.js';
import { isFeatureEnabled } from '@utils/featureFlags.js';
import { mapState, mapActions } from 'pinia';
import { useAuthStore } from '@stores/auth';
import { getMonthDayTime } from '@models/timeformats.js';
import { colors } from 'quasar';
import { useNotificationsStore } from '@stores/notifications.ts';

export default {
    name: 'PhaseCard',
    components: {
        Banner,
        CauseCard,
        CauseTableSelect,
        DateTimePicker,
        MissingCauseLabel,
        ProductCard,
        TableSelect,
        MediaGalleryButton,
        MediaGridManager,
        MediaInputManager
    },
    emits: [
        'triggerSelected',
        'save',
        'refresh',
        'return',
        'refreshList',
        'split',
        'showDescription'
    ],
    props: {
        /**
         * General Object fields
         */
        facilityId: {
            type: [Number, String],
            required: false
        },
        assetId: {
            type: [Number, String],
            required: false
        },
        /**
         * Phase fields
         */
        start: {
            type: String,
            required: false
        },
        end: {
            type: [String, null],
            required: false,
            default: null
        },
        phaseId: {
            type: [Number, String],
            required: true
        },
        phaseUUID: {
            type: String,
            required: true
        },
        phase: {
            type: String,
            required: false
        },
        status: {
            type: String,
            required: false
        },
        cause: {
            type: [Number, String],
            required: false
        },
        causeName: {
            type: String,
            required: false
        },
        product: {
            type: [Number, String],
            required: false
        },
        productName: {
            type: String,
            required: false
        },
        description: {
            type: String,
            required: false
        },
        proximityReading: {
            type: [Number, String],
            required: false,
            default: null
        },
        productDuration: {
            type: String,
            required: false
        },
        phaseDuration: {
            type: String,
            required: false
        },
        isMajor: {
            type: Boolean,
            required: false,
            default: false
        },
        hasProximitySensor: {
            type: Boolean,
            required: false,
            default: false
        },
        /**
         * Phase Card Actions
         */
        isSplittable: {
            type: Boolean,
            required: false,
            default: false
        },
        isSelected: {
            type: Boolean,
            required: false,
            default: false
        },
        isMergeMode: {
            type: Boolean,
            required: false,
            default: false
        },
        isBulkUpdateMode: {
            type: Boolean,
            required: false,
            default: false
        },
        productCount: {
            type: String,
            required: false,
            default: null
        },
        productQuantity: {
            type: String,
            required: false,
            default: null
        },
        /**
         * Phase Media
         */
        phaseMedia: {
            type: Array,
            required: false,
            default: () => []
        },
        // Style
        hasBorder: {
            type: Boolean,
            required: false
        },
        hasSquareBorder: {
            type: Boolean,
            required: false
        },
        descriptionMaxLength: {
            type: Number,
            required: false,
            default: 50
        },
        highlightedPhaseId: {
            type: [Number, String],
            required: false,
            default: null
        }
    },
    created() {
        this.initForm();
    },
    data() {
        return {
            ASSET_STATUS_TO_STYLE,
            PROXIMITY_SENSOR_LABELS,
            // time formats
            loading: false,
            timezone: 'local',
            inlineEditMode: false,
            statusOptions: STATUS_OPTIONS,
            form: {
                start: null,
                end: null,
                status: null,
                cause: null,
                product: null,
                description: '',
                proximityReading: null
            },
            formRules: {
                cause: [
                    val => this.validateCause(val) || this.$t('Select a cause')
                ],
                product: [
                    val =>
                        this.validateProduct(val) || this.$t('Select a product')
                ]
            },
            // selectors
            causeSelectProps: {
                url: `/assets/${this.assetId}/causes/`,
                label: this.$t('Cause'),
                dialogTitle: this.$t('Select a cause'),
                searchLabel: this.$t('Search cause'),
                selection: null,
                facilityId: this.facilityId
            },
            productSelectProps: {
                url: `/assets/${this.assetId}/products/`,
                queryParams: { ordering: 'name' },
                label: this.$t('Product'),
                dialogTitle: this.$t('Select a product'),
                searchLabel: this.$t('Search product'),
                selection: null
            },
            proximitySensorOptions: PROXIMITY_SENSOR_OPTIONS,

            // model for cause selection
            selectedCause: {
                label: null,
                value: null
            },
            editPhaseInfoMessage,
            // media
            editableMedia: [],
            updatedMedia: {
                add: [],
                remove: []
            }
        };
    },
    watch: {
        phaseId: {
            handler() {
                this.initForm();
            }
        },
        description: {
            handler() {
                this.initForm();
            }
        },
        end: {
            handler() {
                this.initForm();
            }
        },
        start: {
            handler() {
                this.initForm();
            }
        },
        proximityReading: {
            handler() {
                this.initForm();
            }
        }
    },
    computed: {
        ...mapState(useAuthStore, {
            userLocale: 'getUserLocale'
        }),
        /**
         * Payload to send server to update phase.
         * @return {Object}
         */
        payload() {
            const status = this.form.status.value;
            const payload = {
                phase: status,
                asset: this.assetId,
                start: this.form.start,
                end: this.form.end,
                description: this.form.description,
                cause: null,
                product: null
            };

            if (status === 'running') {
                payload['product'] = this.form.product.value;
            }

            if (status === 'changeover') {
                payload['cause'] = this.form.cause.value;
                payload['product'] = this.form.product.value;
            }

            if (status === 'in_downtime') {
                if (this.form.cause) {
                    payload['cause'] = this.form.cause.value;
                }
                payload['product'] = this.form.product.value;
                payload['proximity_reading'] =
                    this.form.proximityReading === null
                        ? null
                        : this.form.proximityReading;
            }

            if (
                status === 'stopped' &&
                this.form.cause &&
                this.form.cause.value
            ) {
                payload['cause'] = this.form.cause.value;
            }

            return payload;
        },
        /**
         * Selected property for merge mode.
         * @return {Boolean}
         */
        selected: {
            get: function () {
                return this.isSelected;
            },
            set: function (newValue) {
                this.$emit('triggerSelected');
            }
        },
        /**
         * Enable the submit button when the user updates the form.
         * @return {Boolean}
         */
        formHasChanged() {
            return (
                this.dateToIsoString(this.start) !==
                    this.dateToIsoString(this.form.start) ||
                this.dateToIsoString(this.end) !==
                    this.dateToIsoString(this.form.end) ||
                this.status != this.form.status.value ||
                this.cause != this.form.cause?.value ||
                this.product != this.form.product?.value ||
                this.proximityReading != this.form.proximityReading ||
                this.description != this.form.description ||
                this.editableMedia.length !== this.phaseMedia.length ||
                this.updatedMedia.add.length !== 0 ||
                this.updatedMedia.remove.length !== 0
            );
        },
        /**
         * Get query parameters for cause select.
         * In case of stopped phase, only show shutdown causes.
         * @return {Object}
         */
        getCauseQueryParams() {
            const urlParams = { ordering: 'name' };
            if (this.form.status.value === 'stopped') {
                urlParams['type_of_loss'] = 'shutdown';
            }
            return urlParams;
        },
        /**
         * Show a custom message if the selected phase is a stopped phase
         * and no shutdown causes are available.
         * @return {String}
         */
        getNoShutdownCausesMessage() {
            if (this.form.status.value === 'stopped') {
                return this.$t(
                    'No planned shutdown cause is available for this line.'
                );
            }
            return '';
        },
        /**
         * Verify the state of the MEDIA_UPLOAD feature flag
         * @return {Boolean}
         */
        isMediaUploadEnabled() {
            return this.isFeatureEnabled('MEDIA_UPLOAD');
        },
        /*
         * Get card border class based on the phase
         * @return {String} - The border class
         */
        getCardBorderClass() {
            if (this.$q.screen.gt.xs) {
                return '';
            }

            const { getPaletteColor } = colors;
            const phaseColor = ASSET_STATUS_TO_STYLE[this.phase]['color'];
            return `border-left: 10px solid ${getPaletteColor(phaseColor)};`;
        },
        /**
         * Check if this phase card should be highlighted
         * @return {Boolean}
         */
        isHighlighted() {
            return (
                this.highlightedPhaseId &&
                this.phaseId == this.highlightedPhaseId
            );
        }
    },
    methods: {
        isPhaseEditable,
        isPhaseInProgress,
        getUneditableErrorMessage,
        updatePhaseMedia,
        isFeatureEnabled,
        getMonthDayTime,
        ...mapActions(useNotificationsStore, ['resolveNotificationAction']),
        /**
         * Initialize form.
         */
        initForm() {
            this.form.start = this.start;
            this.form.end = this.end;
            this.form.description = this.description;
            this.form.proximityReading =
                this.proximityReading === null ? null : this.proximityReading;
            // set status option
            this.form.status = this.statusOptions.filter(
                option => option.value === this.status
            )[0];
            if (this.cause) {
                this.form.cause = {
                    label: this.causeName,
                    value: this.cause
                };
            }
            if (this.product) {
                this.form.product = {
                    label: this.productName,
                    value: this.product
                };
            }
            // Initialize editable media
            this.editableMedia = this.phaseMedia;
        },
        /**
         * Validate cause field.
         * @param {String} cause
         * @return {Boolean} - Whether the field value is valid or not.
         */
        validateCause(cause) {
            // check whether cause field needs to be validated
            if (this.isMajor && this.form.status.value === 'in_downtime') {
                return Boolean(cause);
            } else if (this.form.status.value === 'changeover') {
                return Boolean(cause);
            } else {
                return true;
            }
        },
        /**
         * Validate product field.
         * @param {String} product
         * @return {Boolean} - Whether the field value is valid or not.
         */
        validateProduct(product) {
            // check whether product field needs to be validated
            if (this.form.status.value !== 'stopped') {
                return Boolean(product);
            } else {
                return true;
            }
        },
        /**
         * Save phase.
         */
        async submit() {
            // Validate product field
            if (!this.validateProduct(this.form.product?.value)) {
                this.$notifyError(
                    this.$t('Please select a product to update this phase.')
                );
                return;
            }

            this.loading = true;
            const errorMessage = this.$t(
                'Failed to update phase at this time. Please try again later.'
            );
            try {
                const filesToUpload = this.updatedMedia.add.map(
                    media => media.file
                );
                const mediaIdsToRemove = this.updatedMedia.remove;

                // Upload media
                if (filesToUpload.length > 0 || mediaIdsToRemove.length > 0) {
                    await updatePhaseMedia(
                        filesToUpload,
                        mediaIdsToRemove,
                        this.phaseId
                    );
                }

                // Then update the phase
                await this.$axios.put(
                    `assetphases/${this.phaseId}/`,
                    this.payload
                );

                // If cause is part of payload, resolve the downtime in the store to
                // remove notification from notifications list
                if (this.payload.cause) {
                    this.resolveNotificationAction(this.phaseUUID);
                }

                // notify user
                this.$notifySuccess(this.$t('Updated phase.'));
                // close dialog & reload list
                this.$emit('save');
                // this.$emit('return');
                this.refreshList();
            } catch (e) {
                const error =
                    e?.response?.data && typeof e.response.data === 'object'
                        ? e.response.data
                        : {};
                if ('uneditable_phase' in error) {
                    this.$notifyError(
                        this.$t(
                            "Phases that have finished more than {editablePeriod} days ago can't be edited.",
                            {
                                editablePeriod: parseInt(
                                    process.env.EDITABLE_PHASE_DURATION_DAYS ||
                                        5 // 5 days
                                )
                            }
                        )
                    );
                } else {
                    this.$notifyError(errorMessage, error);
                }
            } finally {
                // reset media changes
                this.updatedMedia = {
                    add: [],
                    remove: []
                };
                this.loading = false;
                this.inlineEditMode = false;
            }
        },
        /**
         * Click Card to toggle merge Checkbox
         */
        toggleMergeCheckbox() {
            if (this.isMergeMode) {
                this.selected = !this.selected;
            } else {
                this.toggleBulkUpdateCheckbox();
            }
        },
        /**
         * Click Card to toggle bulk update Checkbox
         */
        toggleBulkUpdateCheckbox() {
            if (this.isBulkUpdateMode) {
                this.selected = !this.selected;
            }
        },
        /**
         * Retrieve proximity sensor label from proximity reading.
         * @param {int} reading
         * @return {String} - Proximity sensor label.
         */
        getProximityLabel(reading) {
            if (reading === null || reading === undefined) {
                return null;
            }
            // If the reading can not be mapped to a label, return null
            const label = PROXIMITY_SENSOR_LABELS[reading]?.label;
            return !label ? null : this.$t(label);
        },
        /**
         * Select/ deselect cause and products in table-select component
         * @param {Object} row - Event from table row.
         * @param {Object} props - Table selection props (causeTableSelect, productTableSelect, MissingCauseLabel)
         */
        select(row, props) {
            if (props.selection && props.selection.value === row.id) {
                props.selection = null;
            } else {
                props.selection = {
                    label: row.name,
                    value: row.id
                };
            }
        },
        /**
         * Triggers the refresh list method from the parent component after adding a cause
         */
        refreshList() {
            this.$emit('refreshList');
        },
        /**
         * Convert a given date to ISO String
         * @param {Date} date
         * @return {String|null} - ISO String or null if date is invalid
         */
        dateToIsoString(date) {
            if (!date) {
                return null;
            }
            return new Date(date).toISOString();
        },
        /**
         * Get the background color class for the phase status.
         * @param {String} phase - The phase status
         * @return {String} - The background color class
         */
        getPhaseStatusBgClass(phase) {
            if (this.$q.screen.lt.sm && !this.inlineEditMode) {
                return 'background-grey';
            }
            return `${ASSET_STATUS_TO_STYLE[phase]['backgroundClass']}`;
        }
    }
};
</script>

<style lang="scss" scoped>
// Get the label close to the text below it
.align-duration-label {
    margin-bottom: -4px;
}

.action-buttons {
    width: 160px;
    background-color: $background-grey;
}

.align-value-right {
    width: 120px;
    text-align: right;
}

// Highlighted phase card styling for mobile view
.highlighted-phase-card {
    border: 4px solid $accent !important;
}
</style>
